import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  FloppyDisk,
  Funnel,
  Wrench,
  WhatsappLogo,
  EnvelopeSimple,
  Lightning,
  ShieldCheck,
  Clock,
} from "@phosphor-icons/react";

function formatTime12h(timeStr) {
  if (!timeStr) return "";
  const parts = String(timeStr).trim().split(":");
  if (parts.length < 2) return timeStr;
  let hours = parseInt(parts[0], 10);
  const minutes = parts[1].padStart(2, "0");
  if (isNaN(hours)) return timeStr;
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const formattedHours = String(hours).padStart(2, "0");
  return `${formattedHours}:${minutes} ${ampm}`;
}

export default function Settings() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [activeTab, setActiveTab] = useState("lead"); // "lead" | "service"

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data));
  }, []);

  if (!s) return <div className="text-[#6B7280]">Loading...</div>;

  const set = (k, v) => setS({ ...s, [k]: v });

  const save = async () => {
    setSaving(true);
    setSaveMessage("");
    try {
      const r = await api.patch("/settings", s);
      setS(r.data);
      toast.success("Settings saved");
      setSaveMessage("Settings saved successfully!");
      setTimeout(() => setSaveMessage(""), 3000);
    } catch (e) {
      toast.error("Save failed");
      setSaveMessage("Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 sm:p-10 max-w-7xl mx-auto space-y-8" data-testid="settings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-[#E5E7EB] pb-6">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#6B7280] mb-1">Configuration</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter">System Settings</h1>
          <p className="text-xs text-[#6B7280] mt-1">Configure automated dispatch toggles, anti-ban throttling, and follow-up cadences.</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {saveMessage && (
            <span className={`text-xs font-bold ${saveMessage.includes("Failed") ? "text-red-600" : "text-green-600"}`}>
              {saveMessage}
            </span>
          )}
          <button
            onClick={save}
            data-testid="save-settings-btn"
            disabled={saving}
            className="bg-[#002FA7] text-white font-bold px-6 py-2.5 hover:bg-[#002280] flex items-center gap-2 disabled:opacity-50 transition-colors shadow-xs cursor-pointer"
          >
            <FloppyDisk size={16} weight="bold" /> {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </div>



      {/* Main Module Tabs: Leads vs Service vs WhatsApp Anti-Ban */}
      <div className="flex flex-wrap border-b border-[#E5E7EB] gap-2">
        <button
          type="button"
          onClick={() => setActiveTab("lead")}
          data-testid="tab-lead-settings"
          className={`flex items-center gap-2 px-6 py-3 text-sm font-bold border-b-2 transition-all cursor-pointer ${
            activeTab === "lead"
              ? "border-[#002FA7] text-[#002FA7] bg-[#EEF2FF]/40 font-extrabold"
              : "border-transparent text-[#6B7280] hover:text-[#111827] hover:bg-gray-50"
          }`}
        >
          <Funnel size={18} weight="bold" />
          <span>Lead Settings</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("service")}
          data-testid="tab-service-settings"
          className={`flex items-center gap-2 px-6 py-3 text-sm font-bold border-b-2 transition-all cursor-pointer ${
            activeTab === "service"
              ? "border-[#002FA7] text-[#002FA7] bg-[#EEF2FF]/40 font-extrabold"
              : "border-transparent text-[#6B7280] hover:text-[#111827] hover:bg-gray-50"
          }`}
        >
          <Wrench size={18} weight="bold" />
          <span>Service Settings</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("antiban")}
          data-testid="tab-antiban-settings"
          className={`flex items-center gap-2 px-6 py-3 text-sm font-bold border-b-2 transition-all cursor-pointer ${
            activeTab === "antiban"
              ? "border-amber-600 text-amber-900 bg-amber-50/60 font-extrabold"
              : "border-transparent text-[#6B7280] hover:text-[#111827] hover:bg-gray-50"
          }`}
        >
          <ShieldCheck size={18} weight="bold" className="text-amber-600" />
          <span>WhatsApp Anti-Ban & Pace Controls</span>
          <span className="text-[10px] px-1.5 py-0.2 bg-amber-100 text-amber-800 border border-amber-300 font-mono font-bold rounded-xs">
            {s.whatsapp_delay_min_seconds || 45}s–{s.whatsapp_delay_max_seconds || 90}s
          </span>
        </button>
      </div>

      {/* ======================= TAB 3: WHATSAPP ANTI-BAN & PACE ======================= */}
      {activeTab === "antiban" && (
        <div className="space-y-6">
          <section className="border border-amber-300 bg-white p-6 shadow-xs space-y-6">
            <div className="flex items-start justify-between pb-4 border-b border-amber-200">
              <div className="flex items-center gap-3">
                <span className="p-2.5 bg-amber-100 text-amber-900 border border-amber-300 rounded-xs">
                  <ShieldCheck size={26} weight="fill" />
                </span>
                <div>
                  <h2 className="font-heading font-black text-xl text-amber-950 tracking-tight flex items-center gap-2">
                    WhatsApp Anti-Ban Protection & Throttling Controls
                    <span className="text-xs uppercase font-extrabold px-2.5 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300 rounded-xs">
                      Active
                    </span>
                  </h2>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Controls the pace and working window for automated follow-ups & broadcast campaigns to emulate human behavior and prevent account restriction.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Delay Range Box */}
              <div className="border border-amber-200/80 bg-amber-50/40 p-5 space-y-4">
                <div className="flex items-center gap-2 text-amber-950 font-bold text-sm">
                  <Clock size={18} className="text-amber-700" />
                  <span>Randomized Anti-Ban Delay Range</span>
                </div>
                <p className="text-xs text-gray-600">
                  Injects randomized human-like jitter between consecutive automated messages.
                </p>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest font-bold text-gray-700 block mb-1">
                      Min Delay (Seconds)
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="10"
                        max="300"
                        value={s.whatsapp_delay_min_seconds != null ? s.whatsapp_delay_min_seconds : 45}
                        onChange={(e) => set("whatsapp_delay_min_seconds", e.target.value === "" ? "" : parseInt(e.target.value))}
                        data-testid="setting-wa-delay-min-tab"
                        className="w-full border border-gray-300 px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-amber-500 focus:outline-none bg-white"
                      />
                      <span className="text-xs font-semibold text-gray-500">sec</span>
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] uppercase tracking-widest font-bold text-gray-700 block mb-1">
                      Max Delay (Seconds)
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="20"
                        max="600"
                        value={s.whatsapp_delay_max_seconds != null ? s.whatsapp_delay_max_seconds : 90}
                        onChange={(e) => set("whatsapp_delay_max_seconds", e.target.value === "" ? "" : parseInt(e.target.value))}
                        data-testid="setting-wa-delay-max-tab"
                        className="w-full border border-gray-300 px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-amber-500 focus:outline-none bg-white"
                      />
                      <span className="text-xs font-semibold text-gray-500">sec</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Batch Limit & Business Hours */}
              <div className="border border-amber-200/80 bg-amber-50/40 p-5 space-y-4">
                <div className="flex items-center gap-2 text-amber-950 font-bold text-sm">
                  <Lightning size={18} className="text-amber-700" />
                  <span>Cycle Batching & Operating Hours</span>
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-gray-700 block mb-1">
                    Max Messages Per Batch
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={s.max_messages_per_batch != null ? s.max_messages_per_batch : 3}
                      onChange={(e) => set("max_messages_per_batch", e.target.value === "" ? "" : parseInt(e.target.value))}
                      data-testid="setting-wa-batch-limit-tab"
                      className="w-full sm:w-36 border border-gray-300 px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-amber-500 focus:outline-none bg-white"
                    />
                    <span className="text-xs font-semibold text-gray-500">msgs / batch</span>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1">
                    Limits messages per batch. With a 45s–90s delay between each message, a batch of 3 takes ~3 to 4.5 minutes to complete safely.
                  </p>
                </div>

                <div className="pt-2 border-t border-amber-200">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-bold text-gray-900">
                      Business Hours Only (IST)
                    </label>
                    <label className="relative inline-flex items-center cursor-pointer shrink-0">
                      <input
                        type="checkbox"
                        checked={Boolean(s.business_hours_only !== false)}
                        onChange={(e) => set("business_hours_only", e.target.checked)}
                        data-testid="setting-business-hours-toggle-tab"
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-amber-600"></div>
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock size={16} className="text-amber-700 shrink-0" />
                    <input
                      type="text"
                      placeholder="09:30"
                      value={s.business_hours_start || "09:30"}
                      onChange={(e) => set("business_hours_start", e.target.value)}
                      className="w-20 border border-gray-300 px-2 py-1.5 text-xs font-mono text-center focus:ring-2 focus:ring-amber-500 focus:outline-none bg-white"
                    />
                    <span className="text-xs text-gray-500 font-semibold">to</span>
                    <input
                      type="text"
                      placeholder="19:30"
                      value={s.business_hours_end || "19:30"}
                      onChange={(e) => set("business_hours_end", e.target.value)}
                      className="w-20 border border-gray-300 px-2 py-1.5 text-xs font-mono text-center focus:ring-2 focus:ring-amber-500 focus:outline-none bg-white"
                    />
                    <span className="text-[11px] text-gray-500 font-medium">
                      ({formatTime12h(s.business_hours_start || "09:30")} – {formatTime12h(s.business_hours_end || "19:30")} IST)
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      )}

      {/* ======================= TAB 1: LEADS SETTINGS ======================= */}
      {activeTab === "lead" && (
        <div className="space-y-6">
          {/* Top Automation Toggles inside Lead Tab */}
          <section className="border border-purple-200 bg-purple-50/40 p-5 sm:p-6 shadow-xs space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-purple-200/70">
              <div className="flex items-center gap-2">
                <span className="p-1.5 bg-purple-100 text-purple-800 border border-purple-200">
                  <Lightning size={18} weight="fill" />
                </span>
                <div>
                  <h2 className="font-heading font-black text-base text-purple-950 tracking-tight">Lead Automation Controls</h2>
                  <p className="text-xs text-purple-800/80">Toggle automated dispatching for Lead follow-up and quotation sequences.</p>
                </div>
              </div>
              <span className="text-[10px] uppercase font-extrabold tracking-wider px-2 py-0.5 bg-purple-100 text-purple-800 border border-purple-300">
                Lead Module
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
              {/* Lead WhatsApp Toggle */}
              <div className="bg-white border border-purple-200/80 p-4 flex items-start justify-between gap-3 shadow-2xs">
                <div className="flex items-start gap-2.5">
                  <WhatsappLogo size={22} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <label htmlFor="lead-wa-toggle" className="text-xs font-bold text-gray-900 block cursor-pointer">
                      Automated WhatsApp Follow-ups
                    </label>
                    <p className="text-[11px] text-[#6B7280] leading-tight mt-0.5">
                      Auto-sends WhatsApp follow-ups and quotations when due (requires connected WhatsApp Web).
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-0.5">
                  <input
                    id="lead-wa-toggle"
                    type="checkbox"
                    checked={Boolean(s.auto_send_enabled)}
                    onChange={(e) => set("auto_send_enabled", e.target.checked)}
                    data-testid="lead-auto-send-toggle"
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#002FA7]"></div>
                </label>
              </div>

              {/* Lead Email Toggle */}
              <div className="bg-white border border-purple-200/80 p-4 flex items-start justify-between gap-3 shadow-2xs">
                <div className="flex items-start gap-2.5">
                  <EnvelopeSimple size={22} weight="fill" className="text-blue-600 shrink-0 mt-0.5" />
                  <div>
                    <label htmlFor="lead-email-toggle" className="text-xs font-bold text-gray-900 block cursor-pointer">
                      Automated Email Follow-ups
                    </label>
                    <p className="text-[11px] text-[#6B7280] leading-tight mt-0.5">
                      Auto-sends follow-up emails to lead contacts when due.
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-0.5">
                  <input
                    id="lead-email-toggle"
                    type="checkbox"
                    checked={Boolean(s.email_followup_enabled)}
                    onChange={(e) => set("email_followup_enabled", e.target.checked)}
                    data-testid="lead-email-followup-toggle"
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#002FA7]"></div>
                </label>
              </div>
            </div>
          </section>

          {/* Follow-up cadence */}
          <section className="border border-[#E5E7EB] bg-white p-6 shadow-xs">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2.5 h-2.5 rounded-full bg-[#002FA7]"></span>
              <h2 className="font-heading font-bold text-xl">Standard Lead Follow-up Cadence</h2>
            </div>
            <p className="text-sm text-[#6B7280] mb-4">When each follow-up fires (in hours, relative to the previous step).</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                ["lead_initial_mins", "Initial (Creation)", "mins"],
                ["fu1_hours", "FU-1 (After Initial)", "hrs"],
                ["fu2_hours", "FU-2 (After FU-1)", "hrs"],
                ["fu3_hours", "FU-3 (After FU-2)", "hrs"],
                ["fu4_hours", "FU-4 (After FU-3)", "hrs"],
                ["fu5_hours", "FU-5 (After FU-4)", "hrs"]
              ].map(([k, l, unit]) => (
                <div key={k} className="flex flex-col justify-between">
                  <label className="text-[10px] uppercase tracking-widest font-bold text-[#374151] mb-1">{l}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={s[k] != null ? s[k] : ""}
                      onChange={(e) => set(k, e.target.value === "" ? "" : parseInt(e.target.value))}
                      data-testid={`setting-${k}`}
                      className="w-full border border-[#D1D5DB] px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                    />
                    <span className="text-xs font-semibold text-[#6B7280]">{unit}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Quotation Follow-up cadence */}
          <section className="border border-[#E5E7EB] bg-white p-6 shadow-xs">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2.5 h-2.5 rounded-full bg-[#8B5CF6]"></span>
              <h2 className="font-heading font-bold text-xl">Quotation Follow-up Cadence</h2>
            </div>
            <p className="text-sm text-[#6B7280] mb-4">
              When quotation-specific follow-ups fire once a Quotation has been sent to a lead (in hours, relative to previous stage). Sending a quote automatically transitions normal lead follow-ups to quote follow-ups.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {[
                ["qfu1_hours", "Quote FU-1 (After sent)"],
                ["qfu2_hours", "Quote FU-2 (After Q-FU1)"],
                ["qfu3_hours", "Quote FU-3 (After Q-FU2)"],
                ["qfu4_hours", "Quote FU-4 (After Q-FU3)"],
                ["qfu5_hours", "Quote FU-5 (After Q-FU4)"]
              ].map(([k, l]) => (
                <div key={k} className="flex flex-col justify-between">
                  <label className="text-[10px] uppercase tracking-widest font-bold text-[#374151] mb-1">{l}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={s[k] != null ? s[k] : ""}
                      onChange={(e) => set(k, e.target.value === "" ? "" : parseInt(e.target.value))}
                      data-testid={`setting-${k}`}
                      className="w-full border border-[#D1D5DB] px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-[#8B5CF6] focus:outline-none"
                    />
                    <span className="text-xs font-semibold text-[#6B7280]">hrs</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Auto-Convert to Lost */}
          <section className="border border-[#E5E7EB] bg-white p-6 shadow-xs">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>
              <h2 className="font-heading font-bold text-xl">Lead Auto-Convert to Lost</h2>
            </div>
            <p className="text-sm text-[#6B7280] mb-4">
              Time period after the final follow-up (Follow-up 5 or Quote FU-5) before an unresponsive lead is automatically marked as <strong>Lost</strong>.
            </p>
            <div className="flex flex-col w-full sm:w-72">
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#374151] mb-1">Period after FU-5 / Q-FU5</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  value={s.auto_lost_days != null ? s.auto_lost_days : 30}
                  onChange={(e) => set("auto_lost_days", e.target.value === "" ? "" : parseInt(e.target.value))}
                  data-testid="setting-auto-lost-days"
                  className="w-full border border-[#D1D5DB] px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                />
                <span className="text-xs font-semibold text-[#6B7280]">days</span>
              </div>
              <span className="text-[11px] text-[#6B7280] mt-1.5">Default: 30 days (applies to both regular and quote follow-up sequences)</span>
            </div>
          </section>

          {/* External Sources Sync */}
          <section className="border border-[#E5E7EB] bg-white p-6 shadow-xs">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-600"></span>
              <h2 className="font-heading font-bold text-xl">Lead External Sources Sync</h2>
            </div>
            <p className="text-sm text-[#6B7280] mb-4">How often to automatically pull and sync leads from Meta Ads and IndiaMART (in hours).</p>
            <div className="flex flex-col w-full sm:w-64">
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#374151] mb-1">Sync Interval</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  value={s.sync_interval_hours != null ? s.sync_interval_hours : 1}
                  onChange={(e) => set("sync_interval_hours", e.target.value === "" ? "" : parseInt(e.target.value))}
                  data-testid="setting-sync-interval"
                  className="w-full border border-[#D1D5DB] px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                />
                <span className="text-xs font-semibold text-[#6B7280]">hrs</span>
              </div>
            </div>
          </section>
        </div>
      )}

      {/* ======================= TAB 2: SERVICE SETTINGS ======================= */}
      {activeTab === "service" && (
        <div className="space-y-6">
          {/* Top Automation Toggles inside Service Tab */}
          <section className="border border-emerald-200 bg-emerald-50/40 p-5 sm:p-6 shadow-xs space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-emerald-200/70">
              <div className="flex items-center gap-2">
                <span className="p-1.5 bg-emerald-100 text-emerald-800 border border-emerald-200">
                  <Lightning size={18} weight="fill" />
                </span>
                <div>
                  <h2 className="font-heading font-black text-base text-emerald-950 tracking-tight">Service Automation Controls</h2>
                  <p className="text-xs text-emerald-800/80">Toggle automated dispatching for Service CRM contracts, renewals, and payment reminders.</p>
                </div>
              </div>
              <span className="text-[10px] uppercase font-extrabold tracking-wider px-2 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300">
                Service Module
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
              {/* Service WhatsApp Toggle */}
              <div className="bg-white border border-emerald-200/80 p-4 flex items-start justify-between gap-3 shadow-2xs">
                <div className="flex items-start gap-2.5">
                  <WhatsappLogo size={22} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <label htmlFor="service-wa-toggle" className="text-xs font-bold text-gray-900 block cursor-pointer">
                      Automated WhatsApp Follow-ups
                    </label>
                    <p className="text-[11px] text-[#6B7280] leading-tight mt-0.5">
                      Auto-sends WhatsApp messages for Warranty, AMC, Payment & Expiry reminders.
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-0.5">
                  <input
                    id="service-wa-toggle"
                    type="checkbox"
                    checked={Boolean(s.service_auto_send_enabled)}
                    onChange={(e) => set("service_auto_send_enabled", e.target.checked)}
                    data-testid="service-auto-send-toggle"
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-700"></div>
                </label>
              </div>

              {/* Service Email Toggle */}
              <div className="bg-white border border-emerald-200/80 p-4 flex items-start justify-between gap-3 shadow-2xs">
                <div className="flex items-start gap-2.5">
                  <EnvelopeSimple size={22} weight="fill" className="text-blue-600 shrink-0 mt-0.5" />
                  <div>
                    <label htmlFor="service-email-toggle" className="text-xs font-bold text-gray-900 block cursor-pointer">
                      Automated Email Follow-ups
                    </label>
                    <p className="text-[11px] text-[#6B7280] leading-tight mt-0.5">
                      Auto-sends payment and contract expiry emails to clients when due.
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-0.5">
                  <input
                    id="service-email-toggle"
                    type="checkbox"
                    checked={Boolean(s.service_email_followup_enabled)}
                    onChange={(e) => set("service_email_followup_enabled", e.target.checked)}
                    data-testid="service-email-followup-toggle"
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-700"></div>
                </label>
              </div>
            </div>
          </section>

          {/* Payment Reminder Follow-up cadence */}
          <section className="border border-[#E5E7EB] bg-white p-6 shadow-xs">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-600"></span>
              <h2 className="font-heading font-bold text-xl">Payment Reminder Follow-up Cadence</h2>
            </div>
            <p className="text-sm text-[#6B7280] mb-4">
              When automated payment follow-ups fire once a Payment Reminder has been sent for a service contract (in hours, relative to previous stage).
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {[
                ["pfu1_hours", "Payment FU-1 (After sent)"],
                ["pfu2_hours", "Payment FU-2 (After P-FU1)"],
                ["pfu3_hours", "Payment FU-3 (After P-FU2)"],
                ["pfu4_hours", "Payment FU-4 (After P-FU3)"],
                ["pfu5_hours", "Payment FU-5 (After P-FU4)"]
              ].map(([k, l]) => (
                <div key={k} className="flex flex-col justify-between">
                  <label className="text-[10px] uppercase tracking-widest font-bold text-[#374151] mb-1">{l}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={s[k] != null ? s[k] : ""}
                      onChange={(e) => set(k, e.target.value === "" ? "" : parseInt(e.target.value))}
                      data-testid={`setting-${k}`}
                      className="w-full border border-[#D1D5DB] px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-emerald-600 focus:outline-none"
                    />
                    <span className="text-xs font-semibold text-[#6B7280]">hrs</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Service CRM Cadence - Warranty & AMC (After Start Date) */}
          <section className="border border-[#E5E7EB] bg-white p-6 shadow-xs">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2.5 h-2.5 rounded-full bg-[#002FA7]"></span>
              <h2 className="font-heading font-bold text-xl">Warranty & AMC Follow-up Cadence (Post Start Date)</h2>
            </div>
            <p className="text-sm text-[#6B7280] mb-4">
              When routine satisfaction & onboarding check-in follow-ups fire after a Warranty or AMC contract start date (in days).
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                ["service_start_fu1_days", "Start FU-1 (Days after start)", 7],
                ["service_start_fu2_days", "Start FU-2 (Days after start)", 30],
                ["service_start_fu3_days", "Start FU-3 (Days after start)", 90],
              ].map(([k, l, def]) => (
                <div key={k} className="flex flex-col justify-between">
                  <label className="text-[10px] uppercase tracking-widest font-bold text-[#374151] mb-1">{l}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      value={s[k] != null ? s[k] : def}
                      onChange={(e) => set(k, e.target.value === "" ? "" : parseInt(e.target.value))}
                      data-testid={`setting-${k}`}
                      className="w-full border border-[#D1D5DB] px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                    />
                    <span className="text-xs font-semibold text-[#6B7280]">days</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Service CRM Expiry Cadence - Warranty & AMC (Before Expiration) */}
          <section className="border border-[#E5E7EB] bg-white p-6 shadow-xs">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span>
              <h2 className="font-heading font-bold text-xl">Warranty & AMC Renewal & Expiry Cadence</h2>
            </div>
            <p className="text-sm text-[#6B7280] mb-4">
              When proactive renewal reminders fire before a Warranty or AMC contract expires (in days prior to expiry date).
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {[
                ["service_exp_fu1_days", "Expiry FU-1 (Pre-expiry)", 60],
                ["service_exp_fu2_days", "Expiry FU-2 (Pre-expiry)", 30],
                ["service_exp_fu3_days", "Expiry FU-3 (Pre-expiry)", 15],
                ["service_exp_fu4_days", "Expiry FU-4 (Pre-expiry)", 7],
                ["service_exp_fu5_days", "Expiry FU-5 (Pre-expiry)", 1],
              ].map(([k, l, def]) => (
                <div key={k} className="flex flex-col justify-between">
                  <label className="text-[10px] uppercase tracking-widest font-bold text-[#374151] mb-1">{l}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      value={s[k] != null ? s[k] : def}
                      onChange={(e) => set(k, e.target.value === "" ? "" : parseInt(e.target.value))}
                      data-testid={`setting-${k}`}
                      className="w-full border border-[#D1D5DB] px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-amber-500 focus:outline-none"
                    />
                    <span className="text-xs font-semibold text-[#6B7280]">days</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Service CRM Cadence - Out of Warranty & Out of AMC */}
          <section className="border border-[#E5E7EB] bg-white p-6 shadow-xs">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2.5 h-2.5 rounded-full bg-purple-600"></span>
              <h2 className="font-heading font-bold text-xl">Out of Warranty & Out of AMC Cadence</h2>
            </div>
            <p className="text-sm text-[#6B7280] mb-4">
              When follow-ups fire for expired or Out of Warranty/AMC contracts to drive AMC renewals and conversions (in hours).
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {[
                ["service_oow_fu1_hours", "OOW FU-1", 24],
                ["service_oow_fu2_hours", "OOW FU-2", 72],
                ["service_oow_fu3_hours", "OOW FU-3", 168],
                ["service_oow_fu4_hours", "OOW FU-4", 336],
                ["service_oow_fu5_hours", "OOW FU-5", 720],
              ].map(([k, l, def]) => (
                <div key={k} className="flex flex-col justify-between">
                  <label className="text-[10px] uppercase tracking-widest font-bold text-[#374151] mb-1">{l}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      value={s[k] != null ? s[k] : def}
                      onChange={(e) => set(k, e.target.value === "" ? "" : parseInt(e.target.value))}
                      data-testid={`setting-${k}`}
                      className="w-full border border-[#D1D5DB] px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-purple-600 focus:outline-none"
                    />
                    <span className="text-xs font-semibold text-[#6B7280]">hrs</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Service CRM Cadence - On Call Service */}
          <section className="border border-[#E5E7EB] bg-white p-6 shadow-xs">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2.5 h-2.5 rounded-full bg-teal-600"></span>
              <h2 className="font-heading font-bold text-xl">On Call Service Cadence</h2>
            </div>
            <p className="text-sm text-[#6B7280] mb-4">
              When service follow-ups fire for on-demand/on-call service visits (in hours).
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                ["service_oncall_fu1_hours", "On Call FU-1", 24],
                ["service_oncall_fu2_hours", "On Call FU-2", 72],
                ["service_oncall_fu3_hours", "On Call FU-3", 168],
              ].map(([k, l, def]) => (
                <div key={k} className="flex flex-col justify-between">
                  <label className="text-[10px] uppercase tracking-widest font-bold text-[#374151] mb-1">{l}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      value={s[k] != null ? s[k] : def}
                      onChange={(e) => set(k, e.target.value === "" ? "" : parseInt(e.target.value))}
                      data-testid={`setting-${k}`}
                      className="w-full border border-[#D1D5DB] px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-teal-600 focus:outline-none"
                    />
                    <span className="text-xs font-semibold text-[#6B7280]">hrs</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

    </div>
  );
}
